import { useMemo } from "react";
import { Link } from "react-router-dom";
import { consolidate, trialBalance } from "@core/index";
import { trialBalanceToCsv, downloadCsv } from "@data/index";
import { useAppStore } from "@state/store";
import { useBrand } from "@branding/useBrand";
import { useLedger } from "@ui/useLedger";
import { MoneyText, TieOutBadge, LoadingSkeleton, EmptyState, ErrorState } from "@ui/components";

export function TrialBalance() {
  const { status, error, accounts, entries, entities, base, entityId, scope, scopeName, isEmpty } =
    useLedger();
  const brand = useBrand();
  const load = useAppStore((s) => s.load);

  const tb = useMemo(
    () => trialBalance(entries, accounts, base, scopeName, entityId),
    [entries, accounts, base, entityId, scopeName],
  );
  const group = useMemo(
    () => consolidate(entries, accounts, entities, base),
    [entries, accounts, entities, base],
  );

  function exportCsv() {
    const csv = trialBalanceToCsv(tb, brand.locale);
    downloadCsv(`trial-balance-${scope}.csv`, csv);
  }

  return (
    <section>
      <div className="page-head">
        <div>
          <h1>Trial Balance</h1>
          <div className="sub">
            {scopeName} · it always ties to zero — or tells you exactly where it doesn’t.
          </div>
        </div>
        <div className="actions">
          <button type="button" className="btn secondary" onClick={exportCsv} disabled={isEmpty}>
            Export CSV
          </button>
        </div>
      </div>

      {status === "loading" ? (
        <LoadingSkeleton rows={5} />
      ) : status === "error" ? (
        <ErrorState
          title="Tie-out check failed to run"
          message={
            error ??
            "The consolidation step errored before it could verify balances. No figures shown, to avoid displaying an unverified trial balance."
          }
          onRetry={load}
        />
      ) : isEmpty ? (
        <EmptyState
          glyph="⚖"
          title="Nothing to balance yet"
          message="Once you post entries or import opening balances, the trial balance builds here and checks tie-out on every change."
        >
          <Link className="btn primary" to="/journal">
            Post an entry
          </Link>
        </EmptyState>
      ) : (
        <>
          <div className="card" style={{ marginBottom: "var(--s4)" }}>
            <div className="card-body row between">
              <div className="row">
                <TieOutBadge tiesOut={tb.tiesOut} delta={tb.delta} label="Ties to zero" />
                {scope === "consolidated" ? (
                  <span className="muted tiny">
                    Consolidated · intercompany eliminations{" "}
                    <MoneyText value={group.eliminations} />
                    {group.unmatched.length > 0
                      ? ` · ${group.unmatched.length} unmatched to review`
                      : ""}
                  </span>
                ) : null}
              </div>
              <span className="mono">
                Δ <MoneyText value={tb.delta} parenNegative={false} />
              </span>
            </div>
          </div>

          {scope === "consolidated" && group.unmatched.length > 0 ? (
            <div className="card" style={{ marginBottom: "var(--s4)" }}>
              <div className="card-body">
                <div className="row between">
                  <h3>Intercompany to review</h3>
                  <span className="badge warn">
                    <span className="dot" /> {group.unmatched.length} unmatched
                  </span>
                </div>
                {group.unmatched.map((u) => (
                  <div className="row between mt3" key={u.label}>
                    <span className="muted">{u.label}</span>
                    <MoneyText value={u.amount} />
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div className="card">
            <div className="card-body tight">
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Account</th>
                      <th className="num">Debit</th>
                      <th className="num">Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tb.rows.map((r) => (
                      <tr key={r.code}>
                        <td className="acctcode">{r.code}</td>
                        <td>{r.name}</td>
                        <td className="num">
                          {r.debit.amount ? <MoneyText value={r.debit} /> : "—"}
                        </td>
                        <td className="num">
                          {r.credit.amount ? <MoneyText value={r.credit} /> : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={2}>Total</td>
                      <td className="num">
                        <MoneyText value={tb.debitTotal} />
                      </td>
                      <td className="num">
                        <MoneyText value={tb.creditTotal} />
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
